import React, { useState } from 'react';
import { renderParagraphs } from '../utils/textFormatters';
import ProjectBanner from '../components/project/ProjectBanner';
import ToggleableDetails from '../components/ToggleableDetails';
import useDocumentTitle from '../hooks/useDocumentTitle';
import pluginCommissionProject from '../data/pluginCommissionProject';

// Section Header Component
const SectionHeader = ({ title }) => (
  <div className="mb-8">
    <div className="inline-block">
      <h2 className="text-3xl font-bold text-[#2978C8] dark:text-[#4f9cf9]">{title}</h2>
      <div className="h-1 bg-[#2978C8] dark:bg-[#4f9cf9] mt-2"></div>
    </div>
  </div>
);

// YouTube Icon SVG Component
const YouTubeIcon = () => (
  <svg className="w-5 h-5 mr-2 inline-block" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.615 3.184c-1.25-.5-2.5-.5-3.75-.5H8.135c-1.25 0-2.5 0-3.75.5C1.5 3.684 0 5.184 0 7.5v9c0 2.316 1.5 3.816 4.385 4.316 1.25.5 2.5.5 3.75.5h7.73c1.25 0 2.5 0 3.75-.5C22.5 20.316 24 18.816 24 16.5v-9c0-2.316-1.5-3.816-4.385-4.316zM9.545 15.537V8.463l6.001 3.037-6.001 3.037z"/>
  </svg>
);

// Function to render paragraphs with links
const renderParagraphsWithLinks = (paragraphs) => {
  return paragraphs.map((paragraph, index) => {
    if (paragraph.includes('https://www.patreon.com')) {
      // Extract the URL
      const urlMatch = paragraph.match(/(https:\/\/www\.patreon\.com\/[^\s]+)/);
      if (urlMatch) {
        const url = urlMatch[1];
        const parts = paragraph.split(url);
        
        return (
          <p key={index} className="mb-4 text-lg text-[#333333] dark:text-white leading-relaxed">
            {parts[0]}
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#2978C8] hover:text-[#294C6F] transition-colors"
            >
              {url}
            </a>
            {parts[1]}
          </p>
        );
      }
    }
    return <p key={index} className="mb-4 text-lg text-[#333333] dark:text-white leading-relaxed">{paragraph}</p>;
  });
};

const PluginCommissionProjectPage = () => {
  useDocumentTitle('Plugin Commissions');
  
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Function to handle image clicks
  const handleImageClick = (imageSrc) => {
    if (enlargedImage === imageSrc) {
      setEnlargedImage(null); // Shrink if clicked again
    } else {
      setEnlargedImage(imageSrc); // Enlarge if clicked first time
    }
  };

  return (
    <div className="project-page">
      <ProjectBanner project={pluginCommissionProject} />
      
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Overview Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#2978C8] dark:text-[#4f9cf9]">
            {pluginCommissionProject.titleIntro}
          </h2>
          {renderParagraphsWithLinks(pluginCommissionProject.overview)}
        </div>
        
        {/* Features Section */}
        <div className="mb-16">
          <SectionHeader title="Example Projects" />
          
          {pluginCommissionProject.features && pluginCommissionProject.features.map((feature, index) => (
            <div key={feature.id} className="mb-12">
              <h3 className="text-2xl font-bold text-[#294C6F] dark:text-[#4f9cf9] mb-4">{feature.title}</h3>
              
              {/* Pretext - Added for all features */}
              {feature.pretext && (
                <p className="text-lg text-[#333333] dark:text-white leading-relaxed mb-4">
                  {feature.pretext}
                </p>
              )}
              
              {/* Featured Image - Placed between pretext and description at 75% size */}
              {feature.featuredImage && (
                <div className="my-6 w-full md:w-[75%] mx-auto">
                  <img
                    src={`${process.env.PUBLIC_URL}${feature.featuredImage}`}
                    alt={`Thumbnail for ${feature.title}`}
                    className="w-full h-auto rounded-xl shadow-lg"
                  />
                </div>
              )}
              
              {/* YouTube Video Link */}
              {feature.videoUrl && (
                <div className="mb-6 text-center">
                  <a 
                    href={feature.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#2978C8] hover:text-[#294C6F] transition-colors"
                  >
                    <YouTubeIcon />
                    Watch {feature.title} on YouTube
                  </a>
                </div>
              )}
              
              {/* Description */}
              <div className="text-lg text-[#333333] dark:text-white leading-relaxed mb-6">
                {renderParagraphs(feature.description)}
              </div>
              
              {/* Image Gallery with Toggleable Component - Moved below description */}
              {feature.images && feature.images.length > 0 && (
                <ToggleableDetails 
                  title="View Gallery" 
                  initialOpen={false}
                  customContent={
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {feature.images.map((image, imgIndex) => (
                        <div 
                          key={imgIndex} 
                          className={`cursor-pointer transition-all duration-300 ${
                            enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                          }`}
                          onClick={() => handleImageClick(`${process.env.PUBLIC_URL}${image}`)}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}${image}`}
                            alt={`Image ${imgIndex + 1}`}
                            className={`rounded-lg ${
                              enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                            }`}
                          />
                        </div>
                      ))}
                    </div>
                  }
                />
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Close enlarged image when clicking outside */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 z-40 bg-transparent"
          onClick={() => setEnlargedImage(null)}
        ></div>
      )}
    </div>
  );
};

export default PluginCommissionProjectPage; 